import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Car, ExternalLink, Check, Send } from "lucide-react";
import { PARKING } from "@/constants/testIds";
import { useLang } from "@/i18n/LanguageContext";
import { HydrangeaFlourish, RoseFlourish } from "@/components/wedding/Flourish";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MUSTAMAE_MAP = "https://mustamae.eelk.ee/wp-content/uploads/2025/04/Parkimine-scaled.jpg";

const initial = { full_name: "", personal_code: "", plate: "" };

export const Parking = () => {
  const { t } = useLang();
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.personal_code.trim() || !form.plate.trim()) {
      toast.error(t("parking.errorMissing"));
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/parking`, {
        full_name: form.full_name.trim(),
        personal_code: form.personal_code.trim(),
        plate: form.plate.trim(),
      });
      setDone(true);
      toast.success(t("parking.toastSuccess"));
      setForm(initial);
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : t("parking.errorGeneric"));
    } finally {
      setLoading(false);
    }
  };

  const heading = t("parking.heading");

  return (
    <section id="parking" data-testid={PARKING.section} className="relative py-24 sm:py-32 bg-[color:var(--sunset-50)] overflow-hidden">
      <HydrangeaFlourish className="absolute -top-6 -right-10 w-[100px] sm:w-[140px] md:w-[180px]" opacity={0.75} />
      <RoseFlourish
        className="absolute -bottom-8 -left-6 w-[100px] sm:w-[140px] md:w-[180px] -rotate-3"
        opacity={0.8}
      />

      <div className="relative max-w-7xl mx-auto px-6 sm:px-10">
        <div className="max-w-3xl mb-16">
          <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tighter font-light">
            {heading[0]}<em className="italic">{heading[1]}</em>{heading[2]}
          </h2>
          <p className="mt-6 font-serif italic text-lg sm:text-xl text-black/70 max-w-xl">{t("parking.sub")}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Mustamäe church parking */}
          <div className="border border-black/15 bg-white p-8 sm:p-10 flex flex-col">
            <div className="flex items-center gap-3 text-[color:var(--sunset-600)]">
              <MapPin size={20} />
              <span className="font-overline text-[10px]">Mustamäe</span>
            </div>
            <h3 className="mt-6 font-title text-2xl sm:text-3xl tracking-tight">{t("parking.mustamaeTitle")}</h3>
            <p className="mt-4 font-sans text-base leading-relaxed text-black/70 flex-1">{t("parking.mustamaeText")}</p>
            <a
              data-testid={PARKING.mapLink}
              href={MUSTAMAE_MAP}
              target="_blank"
              rel="noreferrer"
              className="mt-8 inline-flex items-center gap-2 self-start border border-black bg-black text-white px-6 py-3 font-overline text-[11px] hover:bg-[color:var(--sunset-500)] hover:border-[color:var(--sunset-500)] transition-colors"
            >
              {t("parking.mapCta")}
              <ExternalLink size={14} />
            </a>
          </div>

          {/* Cruise Terminal — registration form */}
          <div className="border border-black/15 bg-white p-8 sm:p-10">
            <div className="flex items-center gap-3 text-[color:var(--sunset-600)]">
              <Car size={20} />
              <span className="font-overline text-[10px]">Kruiisiterminal</span>
            </div>
            <h3 className="mt-6 font-title text-2xl sm:text-3xl tracking-tight">{t("parking.terminalTitle")}</h3>

            {done ? (
              <div data-testid={PARKING.success} className="mt-8 relative border border-black p-8">
                <div className="absolute -top-5 -left-5 w-10 h-10 bg-[color:var(--sunset-500)] text-white flex items-center justify-center">
                  <Check size={18} />
                </div>
                <h4 className="font-title text-2xl tracking-tight">{t("parking.successTitle")}</h4>
                <p className="mt-3 font-serif italic text-black/70">{t("parking.successBody")}</p>
                <button
                  onClick={() => setDone(false)}
                  className="mt-6 font-overline text-[11px] border border-black px-5 py-2.5 hover:bg-black hover:text-white transition-colors"
                >
                  {t("parking.another")}
                </button>
              </div>
            ) : (
              <>
                <p className="mt-4 font-sans text-base leading-relaxed text-black/70">{t("parking.terminalText")}</p>
                <form data-testid={PARKING.form} onSubmit={submit} className="mt-8 space-y-6">
                  <div>
                    <label className="font-overline text-[10px] text-black/60">{t("parking.formName")}</label>
                    <input
                      data-testid={PARKING.name}
                      type="text"
                      className="editorial-input"
                      placeholder={t("parking.formNamePh")}
                      value={form.full_name}
                      onChange={(e) => update("full_name", e.target.value)}
                      required
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label className="font-overline text-[10px] text-black/60">{t("parking.formCode")}</label>
                      <input
                        data-testid={PARKING.code}
                        type="text"
                        inputMode="numeric"
                        className="editorial-input"
                        placeholder={t("parking.formCodePh")}
                        value={form.personal_code}
                        onChange={(e) => update("personal_code", e.target.value)}
                        required
                      />
                    </div>
                    <div>
                      <label className="font-overline text-[10px] text-black/60">{t("parking.formPlate")}</label>
                      <input
                        data-testid={PARKING.plate}
                        type="text"
                        className="editorial-input uppercase"
                        placeholder={t("parking.formPlatePh")}
                        value={form.plate}
                        onChange={(e) => update("plate", e.target.value)}
                        required
                      />
                    </div>
                  </div>
                  <button
                    data-testid={PARKING.submit}
                    type="submit"
                    disabled={loading}
                    className="group inline-flex items-center gap-3 border border-black bg-black text-white px-8 py-4 font-overline text-[11px] hover:bg-[color:var(--sunset-500)] hover:border-[color:var(--sunset-500)] transition-colors disabled:opacity-60"
                  >
                    {loading ? t("parking.submitting") : t("parking.submit")}
                    <Send size={14} className="group-hover:translate-x-0.5 transition-transform" />
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
